import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { debounce } from '@med-fe/util';
import { getIsSuccess, getSaveSuccess, getSelectedHOO, getTemplateDetails } from '../../templates-selector';
import styles from './ConfirmModal.module.scss';
import {
  checkTemplateName,
  resetTemplateNameValid,
  renameTemplate,
  updateLiveRange0TemplateSlotDetails,
  updateLiveSpecialRangeTemplateSlotDetails,
  updateTemplateSlotDetails,
  updateTemplateDraftSlotDetails,
  cloneTemplateDetails,
  resetCloneSuccess,
  publishDraft,
} from '../../templates-actions';
import { Modal } from 'antd';

export function useMainInfoEdit(
  form,
  options?: {
    onUpdateSuccess?: () => void;
    overrideCloneName?: boolean;
    fromLiveRanges?: boolean;
    rangeType?: string;
    isSpecialRange?: boolean;
    isRange0?: boolean;
  }
) {
  const dispatch = useDispatch();
  const history = useHistory();
  const details = useSelector(getTemplateDetails);
  const detailsData = details;
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [initialExamsConfiguration, setInitialExamsConfiguration] = useState(null);
  const templateNameValue = detailsData?.templateName || '';
  let hooPart = '';
  const firstSpaceIdx = templateNameValue.indexOf(' ');
  if (firstSpaceIdx > 0) {
    hooPart = templateNameValue.substring(0, firstSpaceIdx);
  } else {
    hooPart = templateNameValue;
  }
  const templateNameValid = useSelector((state) => state.TEMPLATES_MANAGEMENT_REDUCER?.templateNameValid);
  const [templateNameDirty, setTemplateNameDirty] = useState(false);
  const [patternName, setPatternName] = useState(() => {
    if (firstSpaceIdx > 0) return templateNameValue.substring(firstSpaceIdx + 1);
    return '';
  });

  const isSpecialRange = options?.isSpecialRange ?? false;
  const isRange0 = options?.isRange0 ?? false;

  useEffect(() => {
    if (details && Array.isArray(details.examsConfiguration)) {
      setInitialExamsConfiguration(JSON.stringify(details.examsConfiguration));
      setIsSubmitDisabled(true);
    }
  }, [details?.id]);

  useEffect(() => {
    // Enable if:
    // 1. patternName is not empty and changed, and valid
    // 2. OR slots (examsConfiguration) have changed
    // 3. While validation is pending, keep disabled
    let disabled = false;
    const originalPatternName = (() => {
      const idx = (details?.templateName || '').indexOf(' ');
      return idx > 0 ? details?.templateName?.substring(idx + 1) : '';
    })();
    const currentExamsConfig = JSON.stringify(details?.examsConfiguration || []);
    const slotsChanged = initialExamsConfiguration && currentExamsConfig !== initialExamsConfiguration;
    if ((!patternName || patternName === originalPatternName) && !slotsChanged) {
      disabled = true;
    }
    if (templateNameValid === false) {
      disabled = true;
    }
    if (templateNameDirty && (templateNameValid === null || templateNameValid === undefined)) {
      disabled = true;
    }
    setIsSubmitDisabled(disabled);
  }, [
    patternName,
    templateNameValid,
    templateNameDirty,
    details?.templateName,
    details?.examsConfiguration,
    initialExamsConfiguration,
  ]);

  // Computed value for unsaved changes detection
  const hasUnsavedChanges = !isSubmitDisabled;

  // Debounced Redux action for pattern name validation
  const validateTemplateName = useCallback(
    debounce((id, templateName) => {
      dispatch(checkTemplateName({ id, templateName }));
    }, 600),
    [dispatch]
  );

  useEffect(() => {
    dispatch(resetTemplateNameValid());
    let restPart = '';
    if (firstSpaceIdx > 0) restPart = templateNameValue.substring(firstSpaceIdx + 1);
    setPatternName(restPart);
    setTemplateNameDirty(false);
    form.setFieldsValue({ ...details, patternName: restPart });
  }, [templateNameValue]);

  useEffect(() => {
    if (templateNameDirty && patternName && details?.id && hooPart) {
      const templateName = `${hooPart} ${patternName}`;
      validateTemplateName(details.id, templateName);
    }
  }, [patternName, details?.id, hooPart, validateTemplateName, templateNameDirty]);

  const onSubmit = () => {
    const { id } = details;
    const templateName = `${hooPart} ${patternName}`;
    setPatternName(patternName);
    dispatch(renameTemplate({ id, newName: templateName }));
    setIsSubmitDisabled(true);
  };

  const handlePublish = async () => {
    const { patternName: formPatternName } = form.getFieldsValue();
    const templateMaintUploadDtos = details.examsConfiguration.map((exam) => ({
      templateName: `${hooPart} ${formPatternName}`,
      schedulerTemplateID: details.id,
      temptID: details.id,
      rangeNo: 0,
      resourceId: null,
      serviceGroupId: 0,
      temptName: exam.examName,
      temptDisplayName: exam.examName,
      restrictService: null,
      recordedByComputer: null,
      recordedDate: null,
      rotationWeek: null,
      temptItemID: exam.examId,
      templateItmId2: exam.examId,
      temptStartTime: exam.from,
      temptEndTime: exam.to,
      temptDayOfWeek: null,
      temptDuration: exam.durationOfExam,
      temptInterval: null,
      temptSelected: null,
    }));
    const payload = { templateMaintUploadDtos };
    let result;

    Modal.confirm({
      title: 'Confirmation',
      content: 'Are you sure you want to publish this draft as a Template? This action is irreversible.',
      okText: 'Yes',
      cancelText: 'No',
      icon: null,
      closable: true,
      className: styles.confirmModal,
      centered: true,
      onOk: async () => {
        const result = await dispatch(publishDraft(payload));
        if (result?.type === 'PUBLISH_DRAFT') {
          await new Promise((resolve) => setTimeout(resolve, 2000)); // 2s delay
          history.push('/templates-management');
        }
      },
    });
  };

  const handleUpdate = async (isDraft = false, skipConfirmAndRedirect = false) => {
    const { patternName: formPatternName } = form.getFieldsValue();

    // Fetch client IP address
    let clientIP = null;
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      clientIP = data.ip;
    } catch (error) {
      console.warn('Unable to fetch client IP address:', error);
    }

    const templateMaintUploadDtos = details.examsConfiguration.map((exam) => ({
      templateName: `${hooPart} ${formPatternName}`,
      schedulerTemplateID: details.id,
      temptID: details.id,
      rangeNo: detailsData?.rangeNo || 0,
      resourceId: detailsData?.resourceId || null,
      serviceGroupId: 0,
      temptName: exam.examName,
      temptDisplayName: exam.examName,
      restrictService: null,
      recordedByComputer: clientIP,
      recordedDate: null,
      rotationWeek: null,
      temptItemID: exam.examId,
      templateItmId2: exam.examId,
      temptStartTime: exam.from,
      temptEndTime: exam.to,
      temptDayOfWeek: null,
      temptDuration: exam.durationOfExam,
      temptInterval: null,
      temptSelected: null,
      locationId: detailsData?.locationId || null,
    }));
    const payload = { templateMaintUploadDtos };
    let result;
    if (isDraft) {
      result = await dispatch(updateTemplateDraftSlotDetails(payload));
      if (result?.type === 'UPDATE_TEMPLATEDRAFT_SLOT_DETAILS') {
        // notify parent that update succeeded so session highlights can be cleared
        try {
          options?.onUpdateSuccess?.();
        } catch (e) {
          /* ignore */
        }
        if (!skipConfirmAndRedirect) {
          await new Promise((resolve) => setTimeout(resolve, 2000)); // 2s delay
          history.push('/templates-management');
        }
      }
      return result;
    } else {
      // If skipConfirmAndRedirect is true, perform update directly without modal confirmation
      if (skipConfirmAndRedirect && isSpecialRange) {
        result = await dispatch(updateLiveSpecialRangeTemplateSlotDetails(payload));
        if (result?.type === 'UPDATE_LIVE_SPECIALRANGE_TEMPLATE_SLOT_DETAILS') {
          try {
            options?.onUpdateSuccess?.();
          } catch (e) {
            /* ignore */
          }
        }
        return result;
      }

      if (isSpecialRange || isRange0) {
        Modal.confirm({
          title: 'Live Range update confirmation',
          content: `Are you sure you want to update this provider's Live Range? This action can't be undone.`,
          okText: 'Yes',
          cancelText: 'No',
          icon: null,
          closable: true,
          className: styles.confirmModal,
          centered: true,
          onOk: async () => {
            if (isSpecialRange) {
              result = await dispatch(updateLiveSpecialRangeTemplateSlotDetails(payload));
              if (result?.type === 'UPDATE_LIVE_SPECIALRANGE_TEMPLATE_SLOT_DETAILS') {
                try {
                  options?.onUpdateSuccess?.();
                } catch (e) {
                  /* ignore */
                }
                await new Promise((resolve) => setTimeout(resolve, 2000)); // 2s delay
                history.push(`/templates-assignment?tab=liveRanges`);
              }
            } else if (isRange0) {
              Modal.confirm({
                title: 'Range 0 update confirmation',
                content: 'Do you want to apply the change to all days of the same template?',
                okText: 'Yes',
                cancelText: 'No',
                icon: null,
                closable: true,
                className: styles.confirmModal,
                centered: true,
                onOk: async () => {
                  // Apply to all days of the same template
                  const payloadForAllDays = {
                    ...payload,
                    applyToAllDays: true,
                  };
                  result = await dispatch(updateLiveRange0TemplateSlotDetails(payloadForAllDays));
                  if (result?.type === 'UPDATE_LIVE_RANGE0_TEMPLATE_SLOT_DETAILS') {
                    try {
                      options?.onUpdateSuccess?.();
                    } catch (e) {
                      /* ignore */
                    }
                    await new Promise((resolve) => setTimeout(resolve, 7000)); // 7s delay for all days
                    history.push(`/templates-assignment?tab=liveRanges`);
                  }
                },
                onCancel: async () => {
                  // Apply to only this specific day
                  const payloadForSingleDay = {
                    ...payload,
                    applyToAllDays: false,
                  };
                  result = await dispatch(updateLiveRange0TemplateSlotDetails(payloadForSingleDay));
                  if (result?.type === 'UPDATE_LIVE_RANGE0_TEMPLATE_SLOT_DETAILS') {
                    try {
                      options?.onUpdateSuccess?.();
                    } catch (e) {
                      /* ignore */
                    }
                    await new Promise((resolve) => setTimeout(resolve, 2000)); // 2s delay
                    history.push(`/templates-assignment?tab=liveRanges`);
                  }
                },
              });
            }
          },
        });
      } else {
        result = await dispatch(updateTemplateSlotDetails(payload));
        if (result?.type === 'UPDATE_TEMPLATE_SLOT_DETAILS') {
          try {
            options?.onUpdateSuccess?.();
          } catch (e) {
            /* ignore */
          }
          await new Promise((resolve) => setTimeout(resolve, 2000)); // 2s delay
          history.push('/templates-management');
        }
      }
    }
  };

  // HOO Start/End and duration logic (read-only in edit mode)
  const normalizeHour = (timeStr: string, isEnd: boolean, startRaw?: string) => {
    if (!timeStr) return '';

    let hour = 0;
    let minutes = 0;
    let period: 'AM' | 'PM' = 'AM';
    const raw = timeStr.trim();

    // Parse hour and minutes
    if (raw.includes(':')) {
      const [h, m] = raw.split(':');
      hour = parseInt(h, 10);
      minutes = parseInt(m, 10);
    } else {
      if (raw.length <= 2) {
        hour = parseInt(raw, 10);
        minutes = 0;
      } else {
        hour = parseInt(raw.slice(0, -2), 10);
        minutes = parseInt(raw.slice(-2), 10);
      }
    }

    if (!isEnd) {
      // Start time rules: 7–11 AM, else PM
      if (hour >= 7 && hour <= 11) {
        period = 'AM';
      } else {
        period = 'PM';
      }
    } else {
      // End time rules
      const startHour = startRaw ? parseInt(startRaw, 10) : 0;

      if (startHour >= 7 && startHour <= 11) {
        // Start in AM range
        // Use > instead of >= to handle same-hour cases (e.g., "8-8" means 8 AM to 8 PM)
        if (hour > startHour && hour <= 11) {
          period = 'AM'; // same AM block
        } else {
          period = 'PM'; // otherwise PM (including same hour)
        }
      } else {
        // Start is PM → End is always PM unless wrapping
        period = 'PM';
      }
    }

    // Convert to 12-hour format
    if (hour === 0) hour = 12;
    if (hour > 12) hour -= 12;

    return `${hour}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Usage - use computed hooPart from template name (line 39-44) as fallback
  const hooPart1 = detailsData?.hooPart || hooPart || '';
  const [startRaw, endRaw] = hooPart1 ? hooPart1.split('-') : ['', ''];
  const hooStart = normalizeHour(startRaw, false);
  const hooEnd = normalizeHour(endRaw, true, startRaw);

  function parseTime12(str) {
    if (!str) return null;
    const [time, period] = str.split(' ');
    if (!time || !period) return null;
    let [h, m] = time.split(':');
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  }
  let templateDuration = '';
  const startMins = parseTime12(hooStart);
  const endMins = parseTime12(hooEnd);
  if (startMins !== null && endMins !== null && endMins > startMins) {
    templateDuration = String(endMins - startMins);
  }

  // --- CLONE LOGIC (full stateful version) ---
  const hOOSelected = useSelector(getSelectedHOO);
  const overrideCloneName = options?.overrideCloneName ?? false;
  const [isCloneDisabled, setIsCloneDisabled] = useState(false);
  const [selectedHOO, setSelectedHOO] = useState(hOOSelected ?? []);
  const [cloneTemplateDetailsSuccess, setCloneTemplateDetailsSuccess] = useState(false);
  const [updateTemplateDetailsSuccess, setUpdateTemplateDetailsSuccess] = useState(false);
  const isSuccess = useSelector(getIsSuccess);
  const isSaveSuccess = useSelector(getSaveSuccess);

  // Clear any stale clone success flag when this hook mounts so other
  // navigation entry points don't immediately redirect due to leftover state.
  useEffect(() => {
    dispatch(resetCloneSuccess());
  }, [dispatch]);

  // Keep selectedHOO and isCloneDisabled in sync with Redux
  useEffect(() => {
    setSelectedHOO(hOOSelected ?? []);
    setIsCloneDisabled(!hOOSelected || hOOSelected.length === 0);
  }, [hOOSelected]);

  // Track clone/save success
  useEffect(() => {
    setCloneTemplateDetailsSuccess(isSuccess);
    setUpdateTemplateDetailsSuccess(isSaveSuccess);
  }, [isSuccess, isSaveSuccess]);

  const handleClone = async () => {
    if (!detailsData) return;
    const templateMaintUploadDtos = detailsData.examsConfiguration.map((exam) => ({
      templateName: detailsData.templateName,
      schedulerTemplateID: null,
      temptID: detailsData.id,
      rangeNo: 0,
      resourceId: null,
      serviceGroupId: 0,
      temptName: exam.examName,
      temptDisplayName: exam.examName,
      restrictService: null,
      recordedByComputer: null,
      recordedDate: null,
      rotationWeek: null,
      temptItemID: exam.examId,
      templateItmId2: exam.examId,
      temptStartTime: exam.from,
      temptEndTime: exam.to,
      temptDayOfWeek: null,
      temptDuration: exam.durationOfExam,
      temptInterval: null,
      temptSelected: null,
    }));

    const payload = {
      templateMaintUploadDtos,
      hOOSelected,
      overrideCloneName,
    };

    // Dispatch clone request and wait for saga to update clone success flag.
    // Do NOT redirect here immediately — wait for the clone success selector
    // to flip to true so we only navigate when the backend confirms success.
    await dispatch(cloneTemplateDetails(payload));
  };

  // Navigate to templates management only after clone succeeds
  useEffect(() => {
    if (cloneTemplateDetailsSuccess) {
      history.push('/templates-management');
    }
  }, [cloneTemplateDetailsSuccess]);

  return {
    form,
    detailsData,
    isSubmitDisabled,
    hasUnsavedChanges,
    patternName,
    setPatternName,
    templateNameDirty,
    setTemplateNameDirty,
    templateNameValid,
    hooStart,
    hooEnd,
    templateDuration,
    onSubmit,
    handleUpdate,
    handlePublish,
    handleClone,
    isCloneDisabled,
    selectedHOO,
    cloneTemplateDetailsSuccess,
    updateTemplateDetailsSuccess,
  };
}
